import { Fragment, type FC } from 'react';
import { Navigate, Route, useLocation, useMatch } from 'react-router';
import { useOptionalCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RoleGateway } from 'src/components/role-gateway';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { ReportingUploadsDraftedDetail } from 'src/pages/transport/products/drafted/detail';
import { ReportingUploadsDraftedOverview } from 'src/pages/transport/products/drafted/overview';
import { ReportingUploadsUploadedDetail } from 'src/pages/transport/products/uploaded/detail';
import { ReportingUploadsUploadedOverview } from 'src/pages/transport/products/uploaded/overview';

export const TransportProducts: FC = () => {
  const { search, pathname } = useLocation();

  const isNewDesignActive = useIsNewDesignActive();
  const matchesTransportProducts = useMatch('/transport/products/*');
  const matchesReportingProducts = useMatch('/reporting/products/*');

  const capacityManagementRoot = useOptionalCapacityManagementRoot();

  if (!capacityManagementRoot.loaded) {
    return <PageSpinner />;
  }

  // redirect to old location
  if (matchesTransportProducts && !isNewDesignActive)
    return (
      <Navigate
        replace
        to={{
          pathname: pathname.replace('/transport', '/reporting'),
          search,
        }}
      />
    );

  // redirect to new location
  if (matchesReportingProducts && isNewDesignActive)
    return (
      <Navigate
        replace
        to={{
          pathname: pathname.replace('/reporting', '/transport'),
          search,
        }}
      />
    );

  return (
    <RootRoutes>
      {['/transport/products', '/reporting/products'].map((path) => (
        <Fragment key={path}>
          <Route
            path={`${path}/uploaded`}
            element={
              // RoleGateway is needed, because "/rest/uploads/report"
              // doesn't handle 401 and 403, but returns 500
              <RoleGateway allowed={['TSO']}>
                <ReportingUploadsUploadedOverview
                  capacityManagementRoot={capacityManagementRoot}
                />
              </RoleGateway>
            }
          />

          <Route
            path={path}
            element={
              <Navigate replace to={{ pathname: `${path}/uploaded`, search }} />
            }
          />

          <Route
            path={`${path}/uploaded/:rawDataId`}
            element={
              <ReportingUploadsUploadedDetail
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />
          <Route
            path={`${path}/drafted`}
            element={
              <ReportingUploadsDraftedOverview
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />

          <Route
            path={`${path}/drafted/:draftedProductId`}
            element={
              <ReportingUploadsDraftedDetail
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />
        </Fragment>
      ))}

      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};
